import qs from "qs";
import appConfig from "@/appConfig";
import {
  RequestErrorUnauthenticated,
  RequestErrorStatus,
  RequestErrorBadRequest,
  RequestErrorConnection,
  RequestErrorAborted,
  RequestErrorRedirected,
  RequestErrorBadData,
  RequestErrorUnsuccessful,
  RequestErrorPayloadTooLarge,
} from "@errors/RequestError";

const abortControllers = {};

function formatError(error) {
  if (error.isPssError) return error;
  if (error.name === "AbortError") return RequestErrorAborted();
  return RequestErrorConnection();
}

function safeFetch(url, options) {
  return fetch(url, { ...options })
    .then((response) => {
      const result = { data: null, error: null, status: response.status };

      if (response.redirected)
        return {
          ...result,
          error: RequestErrorRedirected({ url: response.url }),
        };

      switch (response.status) {
        case 204 /* No Content */:
          return result;
        case 401 /* Unauthorized */:
          return { ...result, error: RequestErrorUnauthenticated() };
        case 413 /* Payload too large */:
          return { ...result, error: RequestErrorPayloadTooLarge() };
        case 422 /* Unprocessable Entity */:
          return response.json().then((data) => ({
            ...result,
            data,
            error: RequestErrorBadRequest(),
          }));
        default:
          if (!response.ok)
            return { ...result, error: RequestErrorStatus(response.status) };
          return response
            .json()
            .then((data) => ({ ...result, data }))
            .then((result) =>
              result.data.success === false
                ? {
                    ...result,
                    error: RequestErrorUnsuccessful({
                      message: result.data.error,
                    }),
                  }
                : result
            )
            .catch((e) => ({
              ...result,
              error: RequestErrorBadData({ error: "BadResponse", e }),
            }));
      }
    })
    .catch((error) => {
      return {
        data: null,
        error: formatError(error),
        status: null,
      };
    });
}

function getParams(path, queryParams = {}, abortContext = null) {
  let abortController = null;
  if (abortContext) {
    abortController = new AbortController();
    abortControllers[abortContext] = abortController;
  }

  const options = {
    method: "GET",
    mode: appConfig.corsRequired ? "cors" : "same-origin",
    credentials: "include",
    cache: "no-cache",
    signal: abortController && abortController.signal,
  };
  const paramStr = qs.stringify(queryParams, {
    arrayFormat: "brackets",
    addQueryPrefix: true,
  });
  return [`${appConfig.apiRoot}${path}${paramStr}`, options];
}

function deleteParams(path, queryParams = {}, abortContext = null) {
  let abortController = null;
  if (abortContext) {
    abortController = new AbortController();
    abortControllers[abortContext] = abortController;
  }

  const options = {
    method: "DELETE",
    mode: appConfig.corsRequired ? "cors" : "same-origin",
    credentials: "include",
    cache: "no-cache",
    signal: abortController && abortController.signal,
  };
  const paramStr = qs.stringify(queryParams, {
    arrayFormat: "brackets",
    addQueryPrefix: true,
  });
  return [`${appConfig.apiRoot}${path}${paramStr}`, options];
}

function postParams(path, data, abortContext = null) {
  let abortController = null;
  if (abortContext) {
    abortController = new AbortController();
    abortControllers[abortContext] = abortController;
  }

  const body = JSON.stringify(data);
  const options = {
    method: "POST",
    body,
    headers: {
      "content-type": "application/json",
    },
    mode: appConfig.corsRequired ? "cors" : "same-origin",
    credentials: "include",
    cache: "no-cache",
    signal: abortController && abortController.signal,
  };
  return [`${appConfig.apiRoot}${path}`, options];
}

function putParams(path, data, abortContext = null) {
  let abortController = null;
  if (abortContext) {
    abortController = new AbortController();
    abortControllers[abortContext] = abortController;
  }

  const body = JSON.stringify(data);
  const options = {
    method: "PUT",
    body,
    headers: {
      "content-type": "application/json",
    },
    mode: appConfig.corsRequired ? "cors" : "same-origin",
    credentials: "include",
    cache: "no-cache",
    signal: abortController && abortController.signal,
  };
  return [`${appConfig.apiRoot}${path}`, options];
}

function patchParams(path, data, abortContext = null) {
  let abortController = null;
  if (abortContext) {
    abortController = new AbortController();
    abortControllers[abortContext] = abortController;
  }

  const body = JSON.stringify(data);
  const options = {
    method: "PATCH",
    body,
    headers: {
      "content-type": "application/json",
    },
    mode: appConfig.corsRequired ? "cors" : "same-origin",
    credentials: "include",
    cache: "no-cache",
    signal: abortController && abortController.signal,
  };
  return [`${appConfig.apiRoot}${path}`, options];
}

export function abortRequest(abortContext) {
  if (abortControllers[abortContext]) {
    abortControllers[abortContext].abort();
    delete abortControllers[abortContext];
  }
}

export function fetchGet(...args) {
  return fetch(...getParams(...args));
}
export function fetchDelete(...args) {
  return fetch(...deleteParams(...args));
}
export function fetchPost(...args) {
  return fetch(...postParams(...args));
}
export function fetchPut(...args) {
  return fetch(...putParams(...args));
}
export function fetchPatch(...args) {
  return fetch(...patchParams(...args));
}

export function safeFetchGet(...args) {
  return safeFetch(...getParams(...args));
}
export function safeFetchDelete(...args) {
  return safeFetch(...deleteParams(...args));
}
export function safeFetchPost(...args) {
  return safeFetch(...postParams(...args));
}
export function safeFetchPut(...args) {
  return safeFetch(...putParams(...args));
}
export function safeFetchPatch(...args) {
  return safeFetch(...patchParams(...args));
}
